import { type DayCell, type MonthView } from '@arrow/game-engine';
import { LEVELS } from '@arrow/puzzle-schema';
import { useEffect, useRef } from 'react';

import { Link } from '../../app/router.tsx';
import { dailyNumberOf } from '../../app/schedule.ts';
import { Wedge } from '../../components/icons.tsx';
import { dayWords, monthName, plural, WEEKDAY_LETTERS, weekdayName } from './model.ts';

/**
 * A day, printed as a small square: ink when it counted for the streak (a level finished
 * on the day, like a clue block reversed out of ink), keylined on stock when it was only
 * caught up later, open when it wasn't played. Three marks along its foot are the day's
 * levels, Easy · Medium · Hard: filled when solved, outlined when solved later, a short
 * rule when started. Today sits on the vermilion lip. The words are for screen readers;
 * the shape, not the colour, carries every state.
 */
export function DayTile({ cell, thisYear, since }: { cell: DayCell; thisYear?: number; since?: string | null }) {
	const tile = <Tile cell={cell} thisYear={thisYear} since={since} />;
	// A past day opens from the archive (/daily/n): see it, finish it or play it.
	const n = !cell.isToday && !cell.isFuture ? dailyNumberOf(cell.dateKey) : null;
	if (n === null) return tile;
	return (
		<Link to={`/daily/${n}`} className="st-day-a" aria-label={`${dayWords(cell, thisYear)} Open No. ${n}.`}>
			{tile}
		</Link>
	);
}

function Tile({ cell, thisYear, since }: { cell: DayCell; thisYear?: number | undefined; since?: string | null | undefined }) {
	// Before your first Sparrow a day isn't a missed day: it's left blank.
	const before = !!since && cell.dateKey < since && cell.solved === 0;
	const state = cell.isFuture ? 'future' : cell.counted ? 'counted' : cell.solved > 0 ? 'late' : before ? 'before' : 'open';
	const marks = state === 'counted' || state === 'late' || LEVELS.some((l) => cell.marks[l] === 'started');
	return (
		<span className="st-day" data-state={state} data-today={cell.isToday || undefined}>
			<span className="st-day-n" aria-hidden="true">
				{cell.day}
			</span>
			{marks && (
				<span className="st-pips" aria-hidden="true">
					{LEVELS.map((l) => (
						<i key={l} className="st-pip" data-mark={cell.marks[l]} />
					))}
				</span>
			)}
			<span className="sr-only">{dayWords(cell, thisYear, state === 'before')}</span>
		</span>
	);
}

/** One month of days played, with ‹ › to page back to the first month you played. */
export function MonthCalendar({
	view,
	thisYear,
	since,
	canBack,
	canForward,
	onStep,
}: {
	view: MonthView;
	thisYear: number;
	since: string | null;
	canBack: boolean;
	canForward: boolean;
	onStep: (n: number) => void;
}) {
	const title = monthName(view.year, view.month);
	// The steppers stay focusable at the ends (aria-disabled, UX-019): a press there does
	// nothing, and the one you were pressing hands focus to the other when it runs out.
	const backRef = useRef<HTMLButtonElement>(null);
	const nextRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		if (!canBack && document.activeElement === backRef.current && canForward) nextRef.current?.focus();
		if (!canForward && document.activeElement === nextRef.current && canBack) backRef.current?.focus();
	}, [canBack, canForward]);
	return (
		<div className="st-month">
			<div className="st-month-head">
				<button
					ref={backRef}
					type="button"
					className="st-step"
					aria-label="Previous month"
					aria-disabled={!canBack || undefined}
					onClick={() => canBack && onStep(-1)}
				>
					<Wedge direction="left" />
				</button>
				<p className="st-month-title" aria-live="polite">
					<span className="st-month-name">{title}</span>
					<span className="st-month-sum">
						{view.daysPlayed
							? `${view.daysPlayed} ${plural(view.daysPlayed, 'day')} played · ${view.solved} solved`
							: 'Nothing played this month'}
					</span>
				</p>
				<button
					ref={nextRef}
					type="button"
					className="st-step"
					aria-label="Next month"
					aria-disabled={!canForward || undefined}
					onClick={() => canForward && onStep(1)}
				>
					<Wedge direction="right" />
				</button>
			</div>
			<table className="st-cal">
				<caption className="sr-only">Days played in {title}</caption>
				<thead>
					<tr>
						{WEEKDAY_LETTERS.map((d, i) => (
							<th key={i} scope="col" abbr={weekdayName(i)}>
								<span aria-hidden="true">{d}</span>
								<span className="sr-only">{weekdayName(i)}</span>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{view.weeks.map((week, w) => (
						<tr key={w}>
							{week.map((cell, i) => (
								<td key={i}>{cell ? <DayTile cell={cell} thisYear={thisYear} since={since} /> : null}</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/** What the tiles mean, drawn with the tiles themselves. */
export function CalendarKey({ late }: { late: boolean }) {
	return (
		<ul className="st-key" aria-label="Key">
			<li>
				<span className="st-key-tile" data-state="counted" aria-hidden="true" />
				Played on the day
			</li>
			{late && (
				<li>
					<span className="st-key-tile" data-state="late" aria-hidden="true" />
					Caught up later
				</li>
			)}
			<li>
				<span className="st-pips st-key-pips" aria-hidden="true">
					<i className="st-pip" data-mark="solved" />
					<i className="st-pip" data-mark="solved" />
					<i className="st-pip" data-mark="solved" />
				</span>
				Easy · Medium · Hard
			</li>
		</ul>
	);
}
